import { JSX, JSXElement } from 'solid-js';
import Base from '~/components/Layout/Header/SwitcherButtons/Base.tsx';
import Menu from '~/components/ui/Menu';
import { ChevronsUpDownIcon, SettingsIcon } from 'lucide-solid';
import { Localized } from '~/i18n';
import styles from '../switcher.module.scss';
import { useAppData } from '~/components/AppData';
import { EnvironmentData, EnvironmentTypes } from '~/api';
import { useNavigate, useParams } from '@solidjs/router';
import {
  EnvironmentPagePath,
  getSlug,
  replacePathParams,
  WorkspacePagePath
} from '~/components/AppRouter/utils.ts';
import {
  getAppliedClasses,
  getFullPathWithoutParams
} from '~/utils/commonUtils.ts';
import { miscConstants } from '~/consts.ts';
import EnvironmentSwitcherItem from '~/components/Layout/Header/SwitcherButtons/EnvironmentSwitcher/EnvironmentSwitcherItem.tsx';
import { ItemType } from '~/components/ui/Menu/Menu.tsx';
import EnvironmentIcon from '~/components/Layout/Header/SwitcherButtons/EnvironmentSwitcher/EnvironmentIcon.tsx';
import { twMerge } from 'tailwind-merge';
import { Button } from '~/components/ui/Button';

export default function EnvironmentSwitcher(): JSXElement {
  const appData = useAppData();
  const params = useParams();
  const navigate = useNavigate();

  const matchedRoutePattern = getFullPathWithoutParams({
    paths: Object.values(EnvironmentPagePath),
    params
  });

  function switchEnvironment(envId: string) {
    if (params.environmentId === envId) return;
    localStorage.setItem(
      miscConstants.localStorageKeys.getEnvironmentSelection(),
      envId
    );
    const appPath = replacePathParams(
      matchedRoutePattern as EnvironmentPagePath,
      {
        ...params,
        environmentId: envId
      }
    );
    window.location.assign(`${window.location.origin}${appPath}`);
  }

  const devEnvironments = appData.environments.filter(
    (env) => env.type !== EnvironmentTypes.PRODUCTION
  );

  const prodEnvironments = appData.environments.filter(
    (env) => env.type === EnvironmentTypes.PRODUCTION
  );

  const getEnvironmentList = (
    environments: EnvironmentData[]
  ): Array<ItemType> =>
    environments.map((env, index) => ({
      label: (
        <EnvironmentSwitcherItem
          onClick={() => switchEnvironment(env.id)}
          isActive={appData.selectedEnvironment?.id === env.id}
          environment={env}
        />
      ),
      class: twMerge(
        'mx-1 hover:bg-transparent h-auto min-w-[280px]',
        getAppliedClasses({
          '[&>div]:mb-0':
            index === environments.length - 1 &&
            env.type === EnvironmentTypes.PRODUCTION
        })
      ),
      dataTestId: `environment-switch`
    }));

  return (
    <Menu
      id={`environment-switcher`}
      containerClass={`max-h-[370px] overflow-y-auto relative pb-14`}
      groups={[
        {
          label: (
            <div
              class={`flex justify-between items-center ${styles.menuCategoryText} mt-2`}
            >
              <Localized translationKey={`top_nav_group_labels.environments`} />
            </div>
          ),
          hasGroupEndSeparator: false
        },
        {
          class: 'mb-0.5 mt-2',
          items: [
            ...getEnvironmentList(devEnvironments),
            ...getEnvironmentList(prodEnvironments)
          ],
          hasGroupEndSeparator: false
        },
        {
          label: (
            <Button
              class={`w-full justify-start px-2 hover:bg-bg-subtle`}
              variant={`ghost`}
              onClick={() => {
                navigate(
                  getSlug.workspace(
                    WorkspacePagePath.WORKSPACE_MANAGE_ENVIRONMENTS,
                    true
                  )
                );
              }}
            >
              <div
                class={`flex items-center gap-2 text-fg-default font-normal self-start`}
              >
                <SettingsIcon
                  width={16}
                  height={16}
                  strokeWidth={1}
                />
                <Localized
                  translationKey={`top_nav_group_labels.manage_environments`}
                />
              </div>
            </Button>
          ),
          class:
            'fixed w-[calc(100%+2rem)] bottom-0 z-10 bg-bg-default py-1 border-t border-border-subtle rounded-b-md font-medium'
        }
      ]}
    >
      {(props: JSX.HTMLAttributes<any>) => (
        <Base
          elementAttributes={props}
          dataTestId={`environment-switcher-icon`}
        >
          <div class={`flex w-full items-center`}>
            <EnvironmentIcon
              environmentType={
                appData.selectedEnvironment?.type ||
                EnvironmentTypes.DEVELOPMENT
              }
              class={`p-1 mr-2`}
            />
            <span class={`mr-2`}>
              {appData.selectedEnvironment?.displayName}
            </span>
            <ChevronsUpDownIcon strokeWidth={1} />
          </div>
        </Base>
      )}
    </Menu>
  );
}
